import { BrowserRouter, Routes, Route, useNavigate } from "react-router-dom";
import { requestPermissionAndToken, onForegroundMessage } from "./firebase";
import { api } from "./services/api";
import ProtectedRoute from "./context/ProtectedRoute";
import { AuthProvider, useAuth } from "./context/AuthContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";
import ProfilePage from "./pages/ProfilePage";      // ✅ added
import EditProfilePage from "./pages/EditProfilePage"; 
import SettingsPage from "./pages/SettingsPage";
import PaymentHistory from "./pages/PaymentHistory";
import PaymentSuccess from "./pages/PaymentSuccess"; // ✅ PayMongo redirect handler
import Landing from "./pages/Landing";
import Pricing from "./pages/Pricing";
import UserPricing from "./pages/UserPricing";
import TermsOfService from "./pages/TermsOfService";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import RefundPolicy from "./pages/RefundPolicy";
import FAQ from "./pages/FAQ";
import WhySiteScope from "./pages/WhySiteScope";
import BlogIndex from "./pages/BlogIndex";
import BlogPost from "./pages/BlogPost";
import AdminAnalytics from "./pages/AdminAnalytics";
import MonitorForm from "./components/MonitorForm";
import MonitorList from "./components/MonitorList";
import DashboardLayout from "./components/layout/DashboardLayout";
import InstallPWA from "./components/InstallPWA";
import CookieBanner from "./components/CookieBanner";
import { useEffect, useState } from "react";
import "./utils/testApi"; // Import API test utility
import { initGA } from "./utils/analytics";

async function registerPushTokenOnce() {
  const already = localStorage.getItem("push_registered_v1");
  const jwt = localStorage.getItem("token");
  if (!jwt) return; // only register for logged-in users

  // you can skip this guard if your backend uses ON CONFLICT on token
  if (already) return;

  const token = await requestPermissionAndToken();
  if (token) {
    await api("/save-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token }),
    });
    localStorage.setItem("push_registered_v1", "1");
  }
}

function Dashboard() {
  const [bump, setBump] = useState(0);
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isLoading, setIsLoading] = useState(true);

  // Force reload user from localStorage on mount
  useEffect(() => {
    console.log("🏠 Dashboard mounted, checking user state...");
    console.log("   user from context:", user ? user.email : "NULL");
    console.log("   user from localStorage:", localStorage.getItem("user") ? "EXISTS" : "MISSING");
    
    // Small delay to ensure context has time to initialize
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 100);
    
    return () => clearTimeout(timer);
  }, [user]);

  const handleLogout = () => {
    logout();              // remove token from localStorage
    localStorage.removeItem("push_registered_v1"); // so next login re-registers
    navigate("/login");    // redirect to login
  };

  // Show loading state while user data is being fetched
  if (isLoading || !user) {
    return (
      <DashboardLayout onLogout={handleLogout}>
        <div className="flex justify-center items-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600 dark:text-gray-400">Loading your dashboard...</span>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout onLogout={handleLogout}>
      <div className="space-y-4 sm:space-y-6 max-w-full">
        <MonitorForm onSiteAdded={() => setBump(v => v + 1)} />
        <MonitorList key={bump}/>
      </div>
    </DashboardLayout>
  );
}

export default function App() {
  useEffect(() => {
    // Initialize Google Analytics
    initGA();
    
    // run on every app load for users with persisted sessions
    registerPushTokenOnce();
   // optional: handle messages while the app is open
   onForegroundMessage((p) => {
    console.log("Foreground push:", p);
      if (Notification.permission === "granted") {
        new Notification(p.notification.title, { body: p.notification.body });
      }
    });

    // Fix ResizeObserver error
    const resizeObserverError = (e) => {
      if (e.message === 'ResizeObserver loop completed with undelivered notifications.') {
        e.stopImmediatePropagation();
      }
    };
    
    window.addEventListener('error', resizeObserverError);
    
    return () => {
      window.removeEventListener('error', resizeObserverError);
    };
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <InstallPWA />
        <CookieBanner />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/why-sitescope" element={<WhySiteScope />} />
          <Route path="/blog" element={<BlogIndex />} />
          <Route path="/blog/:slug" element={<BlogPost />} />
          <Route path="/dashboard" element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/terms-of-service" element={<TermsOfService />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
           <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <ProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/edit-profile"
              element={
                <ProtectedRoute>
                  <EditProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute>
                  <SettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/user-pricing"
              element={
                <ProtectedRoute>
                  <UserPricing />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-history"
              element={
                <ProtectedRoute>
                  <PaymentHistory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payment-success"
              element={
                <ProtectedRoute>
                  <PaymentSuccess />
                </ProtectedRoute>
              }
            />
            {/* Public Legal Pages */}
            <Route path="/faq" element={<FAQ />} />
            <Route
              path="/admin-analytics"
              element={
                <ProtectedRoute>
                  <AdminAnalytics />
                </ProtectedRoute>
              }
            />
            <Route path="/refund-policy" element={<RefundPolicy />} />
            <Route path="/terms" element={<TermsOfService />} />
            <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
